const STORAGE_KEY = "panchali-route-restoration";
const MAX_ROUTE_ENTRIES = 80;
const MAX_RESTORE_ATTEMPTS = 60;
const MIN_RESTORE_ATTEMPTS = 20;
const REQUIRED_STABLE_ATTEMPTS = 6;
const RESTORE_INTERVAL_MS = 75;
const STORE_ROUTE = "/store";
const STORE_RETURN_MAX_AGE_MS = 30 * 60 * 1000;
const STORE_SCROLL_POSITION_KEY = "storeScrollPosition";
const STORE_SCROLL_RETURN_KEY = "storeScrollReturn";

const getEmptyState = () => ({
  routes: {},
  lastListing: null,
});

const getRouteUrl = (location = {}) =>
  `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;

const readState = () => {
  if (typeof window === "undefined") {
    return getEmptyState();
  }

  try {
    const parsedState = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));

    return {
      routes: parsedState?.routes && typeof parsedState.routes === "object"
        ? parsedState.routes
        : {},
      lastListing: parsedState?.lastListing || null,
    };
  } catch {
    return getEmptyState();
  }
};

const writeState = (state) => {
  if (typeof window === "undefined") {
    return;
  }

  const routeEntries = Object.entries(state.routes || {})
    .sort(([, leftEntry], [, rightEntry]) =>
      Number(rightEntry?.updatedAt || 0) - Number(leftEntry?.updatedAt || 0)
    )
    .slice(0, MAX_ROUTE_ENTRIES);

  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...state,
        routes: Object.fromEntries(routeEntries),
      })
    );
  } catch {
    // Browsing still works when storage is unavailable.
  }
};

const readStoreReturn = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return JSON.parse(window.sessionStorage.getItem(STORE_SCROLL_RETURN_KEY));
  } catch {
    return null;
  }
};

const writeStoreReturn = (returnState) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      STORE_SCROLL_POSITION_KEY,
      String(returnState.scrollY)
    );
    window.sessionStorage.setItem(
      STORE_SCROLL_RETURN_KEY,
      JSON.stringify(returnState)
    );
  } catch {
    // Browsing still works when storage is unavailable.
  }
};

const updateRouteEntry = (routeKey, update) => {
  if (!routeKey) {
    return;
  }

  const state = readState();
  const currentEntry = state.routes[routeKey] || {};

  state.routes[routeKey] = {
    ...currentEntry,
    ...update,
    updatedAt: Date.now(),
  };

  writeState(state);
};

const getCurrentScrollPosition = () => {
  if (typeof window === "undefined") {
    return { x: 0, y: 0 };
  }

  const scrollingElement =
    document.scrollingElement || document.documentElement;

  return {
    x: window.scrollX || scrollingElement.scrollLeft || 0,
    y: window.scrollY || scrollingElement.scrollTop || document.body.scrollTop || 0,
  };
};

const setDocumentScrollPosition = ({ x = 0, y = 0 }) => {
  const scrollingElement =
    document.scrollingElement || document.documentElement;

  window.scrollTo(x, y);
  scrollingElement.scrollLeft = x;
  scrollingElement.scrollTop = y;
  document.body.scrollLeft = x;
  document.body.scrollTop = y;
};

export const saveCurrentScrollPosition = (routeKey) => {
  updateRouteEntry(routeKey, {
    scroll: getCurrentScrollPosition(),
  });
};

export const saveListingReturnPosition = (
  location,
  { productId = "", cardElement = null } = {}
) => {
  if (location?.pathname !== STORE_ROUTE) {
    return;
  }

  const scrollPosition = getCurrentScrollPosition();
  const returnState = {
    url: getRouteUrl(location),
    productId: String(productId || ""),
    cardViewportTop: cardElement?.getBoundingClientRect?.().top ?? null,
    scrollY: scrollPosition.y,
    pending: true,
    updatedAt: Date.now(),
  };

  writeStoreReturn(returnState);
};

export const getStoreListingReturnPosition = (location) => {
  const lastListing = readStoreReturn();
  const isFresh =
    Date.now() - Number(lastListing?.updatedAt || 0) <= STORE_RETURN_MAX_AGE_MS;

  if (
    location?.pathname !== STORE_ROUTE
    || !lastListing?.pending
    || !isFresh
    || lastListing.url !== getRouteUrl(location)
  ) {
    return null;
  }

  const storedScrollY = Number(
    window.sessionStorage.getItem(STORE_SCROLL_POSITION_KEY)
  );

  return Number.isFinite(storedScrollY)
    ? {
        ...lastListing,
        scroll: {
          x: 0,
          y: storedScrollY,
        },
      }
    : null;
};

export const completeStoreListingReturn = (location) => {
  const lastListing = readStoreReturn();

  if (
    lastListing?.pending
    && lastListing.url === getRouteUrl(location)
  ) {
    try {
      window.sessionStorage.removeItem(STORE_SCROLL_POSITION_KEY);
      window.sessionStorage.removeItem(STORE_SCROLL_RETURN_KEY);
    } catch {
      // Browsing still works when storage is unavailable.
    }
  }
};

export const saveCarouselPosition = (routeKey, carouselId, activeIndex) => {
  if (!carouselId || !Number.isFinite(Number(activeIndex))) {
    return;
  }

  const state = readState();
  const currentEntry = state.routes[routeKey] || {};

  updateRouteEntry(routeKey, {
    carousels: {
      ...(currentEntry.carousels || {}),
      [carouselId]: Number(activeIndex),
    },
  });
};

export const getCarouselPosition = (routeKey, carouselId) => {
  const activeIndex = readState().routes[routeKey]?.carousels?.[carouselId];

  return Number.isFinite(Number(activeIndex)) ? Number(activeIndex) : 0;
};

export const resetDocumentScrollPosition = () => {
  setDocumentScrollPosition({ x: 0, y: 0 });

  const frameId = window.requestAnimationFrame(() => {
    setDocumentScrollPosition({ x: 0, y: 0 });
  });

  return () => {
    window.cancelAnimationFrame(frameId);
  };
};

export const restoreDocumentScrollPosition = (
  position,
  { getTargetPosition = null, onComplete = null, onUserCancel = null } = {}
) => {
  if (!position || typeof window === "undefined") {
    return () => {};
  }

  let attemptCount = 0;
  let stableAttemptCount = 0;
  let previousDocumentHeight = 0;
  let frameId = null;
  let timeoutId = null;
  let cancelled = false;

  const cancel = () => {
    cancelled = true;

    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    window.removeEventListener("wheel", cancelForUserInteraction);
    window.removeEventListener("touchstart", cancelForUserInteraction);
    window.removeEventListener("pointerdown", cancelForUserInteraction);
    window.removeEventListener("keydown", cancelForUserInteraction);
  };

  const cancelForUserInteraction = () => {
    cancel();
    onUserCancel?.();
  };

  const scheduleAttempt = () => {
    timeoutId = window.setTimeout(() => {
      frameId = window.requestAnimationFrame(restore);
    }, RESTORE_INTERVAL_MS);
  };

  const restore = () => {
    if (cancelled) {
      return;
    }

    attemptCount += 1;
    const targetPosition = getTargetPosition?.() || position;
    setDocumentScrollPosition(targetPosition);

    const scrollingElement =
      document.scrollingElement || document.documentElement;
    const documentHeight = scrollingElement.scrollHeight;
    const maxScrollY = Math.max(0, documentHeight - window.innerHeight);
    const hasEnoughHeight = maxScrollY >= targetPosition.y;
    const reachedPosition =
      Math.abs(getCurrentScrollPosition().y - Math.min(targetPosition.y, maxScrollY)) <= 1;

    if (
      hasEnoughHeight
      && reachedPosition
      && documentHeight === previousDocumentHeight
    ) {
      stableAttemptCount += 1;
    } else {
      stableAttemptCount = 0;
    }

    previousDocumentHeight = documentHeight;

    const isStable =
      attemptCount >= MIN_RESTORE_ATTEMPTS
      && stableAttemptCount >= REQUIRED_STABLE_ATTEMPTS;

    if (isStable || attemptCount >= MAX_RESTORE_ATTEMPTS) {
      cancel();
      onComplete?.();
      return;
    }

    scheduleAttempt();
  };

  window.addEventListener("wheel", cancelForUserInteraction, { passive: true });
  window.addEventListener("touchstart", cancelForUserInteraction, { passive: true });
  window.addEventListener("pointerdown", cancelForUserInteraction, { passive: true });
  window.addEventListener("keydown", cancelForUserInteraction);

  frameId = window.requestAnimationFrame(restore);

  return cancel;
};

export const restoreStoreListingPosition = (returnPosition, onComplete) => {
  let didFinish = false;
  const finish = () => {
    if (didFinish) {
      return;
    }

    didFinish = true;
    onComplete?.();
  };

  return restoreDocumentScrollPosition(returnPosition?.scroll, {
    getTargetPosition: () => {
      const cardElement = Array.from(
        document.querySelectorAll("[data-store-product-id]")
      ).find(
        (element) =>
          element.dataset.storeProductId === String(returnPosition?.productId || "")
      );

      if (!cardElement || !Number.isFinite(Number(returnPosition?.cardViewportTop))) {
        return returnPosition?.scroll;
      }

      const currentPosition = getCurrentScrollPosition();

      return {
        x: returnPosition.scroll.x,
        y: Math.max(
          0,
          currentPosition.y
            + cardElement.getBoundingClientRect().top
            - Number(returnPosition.cardViewportTop)
        ),
      };
    },
    onComplete: finish,
    onUserCancel: finish,
  });
};
